import { Helmet } from "react-helmet-async";
import Layout from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Users, Target, Shield, Award } from "lucide-react";

const About = () => {
  return (
    <Layout>
      <Helmet>
        <title>About Us — Datalevo Tools | Walking Weight Loss Calculator</title>
        <meta
          name="description"
          content="Learn about Datalevo and our mission to create privacy-first, evidence-based health and fitness tools. Meet the team behind the Walking Weight Loss Calculator."
        />
        <link rel="canonical" href="https://walkingweightlosscalculator.datalevo.com/about" />
      </Helmet>

      <div className="container mx-auto px-4 py-12 md:py-16">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            About Datalevo
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            We build privacy-first, evidence-based tools that help people make informed decisions about their health and fitness journey.
          </p>
        </div>

        {/* Mission Section */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <Card className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-primary/10">
                <Target className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Our Mission</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              To democratize access to accurate health calculations and planning tools. We believe everyone deserves access to the same quality of analysis that was once only available through expensive consultations or complex spreadsheets.
            </p>
          </Card>

          <Card className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-accent/10">
                <Shield className="h-6 w-6 text-accent" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Privacy First</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Your health data belongs to you. All our calculations run locally in your browser—no data is sent to any server unless you explicitly choose to save or share your results. We never sell or share your information.
            </p>
          </Card>
        </div>

        {/* Values Section */}
        <div className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-8">
            Our Core Values
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Shield,
                title: "Privacy",
                description: "Client-side processing by default. Your data stays yours.",
              },
              {
                icon: Award,
                title: "Accuracy",
                description: "Validated formulas from peer-reviewed research and health organizations.",
              },
              {
                icon: Users,
                title: "Accessibility",
                description: "Free tools for everyone, designed with WCAG accessibility in mind.",
              },
              {
                icon: Target,
                title: "Transparency",
                description: "Open methodology with citations. Understand how every calculation works.",
              },
            ].map((value) => (
              <Card key={value.title} className="p-5 text-center">
                <div className="inline-flex p-3 rounded-full bg-primary/10 mb-3">
                  <value.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{value.title}</h3>
                <p className="text-sm text-muted-foreground">{value.description}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Team Section */}
        <div className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-8">
            Meet Our Team
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Creator */}
            <Card className="p-6">
              <div className="flex items-start gap-4">
                <Avatar className="h-16 w-16 border-2 border-primary">
                  <AvatarImage src="/assets/images/reviewers/shafi-tareen.png" alt="Shafi Tareen" />
                  <AvatarFallback className="bg-primary text-primary-foreground">ST</AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-foreground">Shafi Tareen</h3>
                    <Badge variant="secondary">Creator</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">Founder, Datalevo</p>
                  <p className="text-sm text-muted-foreground">
                    Passionate about building tools that make complex calculations accessible to everyone.
                  </p>
                </div>
              </div>
            </Card>

            {/* Reviewer */}
            <Card className="p-6">
              <div className="flex items-start gap-4">
                <Avatar className="h-16 w-16 border-2 border-accent">
                  <AvatarImage src="/assets/images/reviewers/nasir-khan.png" alt="Nasir Khan" />
                  <AvatarFallback className="bg-accent text-accent-foreground">NK</AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-foreground">Nasir Khan</h3>
                    <Badge variant="secondary">Reviewer</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">M.Sc., Exercise Physiology</p>
                  <p className="text-sm text-muted-foreground">
                    Ensures all formulas and methodologies meet scientific standards and best practices.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Card className="inline-block p-8 bg-primary/5 border-primary/20">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Ready to Start Your Journey?
            </h2>
            <p className="text-muted-foreground mb-4">
              Try our Walking Weight Loss Calculator and create your personalized plan.
            </p>
            <a
              href="/#calculator"
              className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary-hover transition-colors"
            >
              Start Calculator
            </a>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default About;
